import { Popover } from "@base-ui-components/react/popover"
import cx from "classnames"
import { Ellipsis, Image, Pin, Search, X } from "lucide-react"
import { useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import type { NewTaskAgentOption } from "../newTaskAgentOptions"
import { NEW_TASK_PINNED_AGENT_LIMIT } from "../persistence/newTaskAgentPins"
import type { HarnessId } from "../providerEvents/harnessEventTypes"
import { usePortalContainer } from "../hooks/usePortalContainer"
import { formatModelPricing, matchesModelSearch } from "../modelCatalog"
import { AgentProviderMark, agentProviderVisuals } from "./AgentProviderIdentity"

function QuickAgentButton({
    option,
    disabled,
    primary,
    mobile,
    shortcutIndex,
    onCreate,
}: {
    option: NewTaskAgentOption
    disabled: boolean
    primary: boolean
    mobile: boolean
    shortcutIndex: number
    onCreate: (option: NewTaskAgentOption) => void
}) {
    const visuals = agentProviderVisuals(option.harnessId)
    const shortcut = `alt+${shortcutIndex}`

    useHotkeys(
        shortcut,
        (event) => {
            event.preventDefault()
            onCreate(option)
        },
        {
            enabled: !disabled,
            enableOnContentEditable: true,
            enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"],
        },
        [disabled, onCreate, option]
    )

    return (
        <button
            type="button"
            onClick={() => onCreate(option)}
            disabled={disabled}
            title={`Start with ${option.modelLabel} (⌥${shortcutIndex}${primary ? ", ⌘↵" : ""})`}
            aria-label={`Start task with ${option.modelLabel} on ${option.harnessLabel}`}
            aria-keyshortcuts={`${primary ? "Meta+Enter Control+Enter " : ""}Alt+${shortcutIndex}`}
            data-provider={visuals.id}
            data-quick-agent-index={shortcutIndex}
            className={cx(
                "btn relative flex min-w-0 items-center border text-left font-medium transition-colors",
                mobile ? "min-h-12 flex-1 flex-col justify-center gap-0.5 px-1 pb-1 pt-3" : "min-h-11 min-w-[7.5rem] gap-2 px-3 py-2 pr-7",
                disabled && "cursor-not-allowed opacity-45",
                !disabled && "cursor-pointer",
                visuals.quickButton
            )}
        >
            <span
                className={cx(
                    "pointer-events-none absolute right-1 top-1 font-semibold tabular-nums",
                    mobile ? "text-[8px] leading-none opacity-70" : "text-[9px] leading-none opacity-65"
                )}
                data-agent-shortcut="true"
                aria-hidden="true"
            >
                ⌥{shortcutIndex}
            </span>
            <AgentProviderMark harnessId={option.harnessId} className={cx("shrink-0", mobile ? "h-3.5 w-3.5" : "h-[1.125rem] w-[1.125rem]")} />
            <span className="min-w-0">
                <span className={cx("block truncate", mobile ? "max-w-full text-[10px] leading-3" : "text-sm")}>
                    {option.compactModelLabel}
                    {option.thinking ? ` · ${option.thinking}` : ""}
                </span>
                {!mobile && <span className={cx("block truncate text-[10px]", visuals.quickSecondaryText)}>{option.harnessLabel}</span>}
            </span>
        </button>
    )
}

function ManualAgentOption({
    option,
    disabled,
    pinOrder,
    pinDisabled,
    onCreate,
    onTogglePin,
}: {
    option: NewTaskAgentOption
    disabled: boolean
    pinOrder?: number
    pinDisabled: boolean
    onCreate: (option: NewTaskAgentOption) => void
    onTogglePin: (option: NewTaskAgentOption) => void
}) {
    const visuals = agentProviderVisuals(option.harnessId)
    const pinned = pinOrder !== undefined
    const pinLabel = pinned ? `Unpin ${option.modelLabel} from position ${pinOrder + 1}` : `Pin ${option.modelLabel}`
    const pricing = formatModelPricing(option.pricing)
    return (
        <div className="flex min-w-0">
            <button
                type="button"
                className={cx(
                    "btn flex min-h-14 min-w-0 flex-1 items-center gap-3.5 border border-r-0 px-4 py-2.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-45",
                    visuals.pickerButton
                )}
                disabled={disabled}
                onClick={() => onCreate(option)}
                aria-label={`Start task with ${option.modelLabel} on ${option.harnessLabel}`}
                data-provider={visuals.id}
            >
                <AgentProviderMark harnessId={option.harnessId} className={cx("h-5 w-5 shrink-0", visuals.pickerMark)} />
                <span className="min-w-0">
                    <span className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-semibold text-base-content">
                            {option.modelLabel}
                            {option.thinking ? ` · ${option.thinking}` : ""}
                        </span>
                        {option.legacy && <span className="shrink-0 bg-base-300 px-1.5 py-0.5 text-[10px] font-semibold text-muted">Legacy</span>}
                    </span>
                    <span className="block truncate text-xs text-muted">
                        {option.vendorLabel}
                        {option.nativeProvider ? ` · ${option.nativeProvider}` : ""}
                        {pricing ? ` · ${pricing}` : ""}
                    </span>
                </span>
                {option.supportsImages && <Image size={13} className="ml-auto shrink-0 text-muted" aria-label="Supports image input" />}
            </button>
            <button
                type="button"
                className={cx(
                    "btn flex min-h-14 w-12 shrink-0 items-center justify-center gap-1 border px-2 transition-colors disabled:cursor-not-allowed disabled:opacity-35",
                    visuals.pickerButton,
                    pinned ? visuals.pickerHeading : "text-muted"
                )}
                onClick={() => onTogglePin(option)}
                disabled={pinDisabled}
                aria-label={pinLabel}
                aria-pressed={pinned}
                title={pinDisabled ? `Unpin another model first (${NEW_TASK_PINNED_AGENT_LIMIT} maximum)` : pinLabel}
            >
                {pinned && <span className="text-[10px] font-semibold tabular-nums">{pinOrder + 1}</span>}
                <Pin size={14} className={cx(pinned && "fill-current")} />
            </button>
        </div>
    )
}

export function TaskCreateAgentActions({
    options,
    pinnedOptions,
    disabled,
    mobile = false,
    onCreate,
    onPinnedOptionsChange,
}: {
    options: readonly NewTaskAgentOption[]
    pinnedOptions: readonly NewTaskAgentOption[]
    disabled: boolean
    mobile?: boolean
    onCreate: (option: NewTaskAgentOption) => void
    onPinnedOptionsChange: (options: NewTaskAgentOption[]) => void
}) {
    const portalContainer = usePortalContainer()
    const [query, setQuery] = useState("")
    const pinnedOrderById = new Map(pinnedOptions.map((option, index) => [option.id, index]))
    const pinLimitReached = pinnedOptions.length >= NEW_TASK_PINNED_AGENT_LIMIT
    const optionsByHarness = new Map<HarnessId, NewTaskAgentOption[]>()
    const filteredOptions = options.filter((option) =>
        matchesModelSearch(query, [option.modelId, option.modelLabel, option.compactModelLabel, option.harnessLabel, option.vendorLabel, option.nativeProvider])
    )
    for (const option of filteredOptions) {
        const harnessOptions = optionsByHarness.get(option.harnessId) ?? []
        harnessOptions.push(option)
        optionsByHarness.set(option.harnessId, harnessOptions)
    }

    return (
        <div className={cx("flex min-w-0 items-stretch", mobile ? "w-full gap-1" : "max-w-full gap-1.5 overflow-x-auto")}>
            {pinnedOptions.map((option, index) => (
                <QuickAgentButton
                    key={option.id}
                    option={option}
                    disabled={disabled}
                    primary={index === 0}
                    mobile={mobile}
                    shortcutIndex={index + 1}
                    onCreate={onCreate}
                />
            ))}

            <Popover.Root>
                <Popover.Trigger
                    title="Choose or pin another agent"
                    aria-label="Choose or pin another agent"
                    className={cx(
                        "btn flex shrink-0 items-center justify-center gap-1.5 border border-border bg-base-100 text-muted transition-colors hover:bg-base-200 hover:text-base-content",
                        mobile ? "min-h-12 w-11 px-2" : "min-h-11 px-3 text-xs"
                    )}
                >
                    <Ellipsis size={16} />
                    {!mobile && <span>Other</span>}
                </Popover.Trigger>
                <Popover.Portal container={portalContainer}>
                    <Popover.Positioner side="top" align="end" sideOffset={8} className="z-50">
                        <Popover.Popup className="w-[min(34rem,calc(100vw-1rem))] border border-border bg-base-100 shadow-lg outline-none">
                            <div className="border-b border-border px-4 py-3">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="text-sm font-semibold text-base-content">Choose or pin an agent</div>
                                    <div className="shrink-0 text-[11px] font-semibold text-muted">
                                        {pinnedOptions.length}/{NEW_TASK_PINNED_AGENT_LIMIT} pinned
                                    </div>
                                </div>
                                <div className="mt-1 text-xs leading-5 text-muted">Choose a model to start now. Pins become the quick row in pin order.</div>
                                {pinnedOptions.length > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-1.5" aria-label="Pinned agents">
                                        {pinnedOptions.map((option) => (
                                            <button
                                                key={option.id}
                                                type="button"
                                                aria-label={`Remove pinned ${option.modelLabel}${option.thinking ? ` · ${option.thinking}` : ""}`}
                                                onClick={() => onPinnedOptionsChange(pinnedOptions.filter((pinned) => pinned.id !== option.id))}
                                                className="flex max-w-full items-center gap-1.5 border border-border px-2 py-1 text-xs text-muted hover:bg-base-200 hover:text-base-content"
                                            >
                                                <AgentProviderMark harnessId={option.harnessId} className="h-3 w-3 shrink-0" />
                                                <span className="truncate">
                                                    {option.compactModelLabel}
                                                    {option.thinking ? ` · ${option.thinking}` : ""}
                                                </span>
                                                <X size={12} className="shrink-0" aria-hidden="true" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <label className="mt-3 flex h-9 items-center gap-2 border border-border bg-base-200 px-3 text-muted focus-within:border-primary">
                                    <Search size={14} aria-hidden="true" />
                                    <input
                                        type="search"
                                        value={query}
                                        onChange={(event) => setQuery(event.target.value)}
                                        placeholder={`Search ${options.length} models`}
                                        aria-label="Search agent models"
                                        className="min-w-0 flex-1 appearance-none border-0 bg-transparent p-0 text-sm text-base-content outline-none placeholder:text-muted"
                                    />
                                </label>
                            </div>
                            <div className="max-h-[min(32rem,65dvh)] space-y-7 overflow-y-auto p-4">
                                {Array.from(optionsByHarness.entries()).map(([harnessId, harnessOptions], sectionIndex) => {
                                    const visuals = agentProviderVisuals(harnessId)
                                    return (
                                        <section
                                            key={harnessId}
                                            data-provider-section={visuals.id}
                                            className={cx(sectionIndex > 0 && "border-t border-border/80 pt-5")}
                                        >
                                            <div
                                                className={cx(
                                                    "mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em]",
                                                    visuals.pickerHeading
                                                )}
                                            >
                                                <AgentProviderMark harnessId={harnessId} className="h-4 w-4" />
                                                <span>{harnessOptions[0]?.harnessLabel ?? harnessId}</span>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 max-[600px]:grid-cols-1">
                                                {harnessOptions.map((option) => {
                                                    const pinOrder = pinnedOrderById.get(option.id)
                                                    return (
                                                        <ManualAgentOption
                                                            key={option.id}
                                                            option={option}
                                                            disabled={disabled}
                                                            pinOrder={pinOrder}
                                                            pinDisabled={pinOrder === undefined && pinLimitReached}
                                                            onCreate={onCreate}
                                                            onTogglePin={(target) => {
                                                                const nextPinned =
                                                                    pinOrder === undefined
                                                                        ? [...pinnedOptions, target].slice(0, NEW_TASK_PINNED_AGENT_LIMIT)
                                                                        : pinnedOptions.filter((pinnedOption) => pinnedOption.id !== target.id)
                                                                onPinnedOptionsChange(nextPinned)
                                                            }}
                                                        />
                                                    )
                                                })}
                                            </div>
                                        </section>
                                    )
                                })}
                                {filteredOptions.length === 0 && <div className="py-8 text-center text-sm text-muted">No models match “{query}”.</div>}
                            </div>
                        </Popover.Popup>
                    </Popover.Positioner>
                </Popover.Portal>
            </Popover.Root>
        </div>
    )
}
